// SellerNetSheet/sellerNet.shared.js
import { useCallback, useState } from "react";

/**
 * INITIAL_VALUES
 * -------------
 * This is the canonical react-hook-form defaultValues shape.
 * Keeping it in one place ensures:
 * - reset() always returns to a known clean state
 * - field arrays have predictable structure
 * - you can add/remove fields safely without hunting through the component
 */
export const INITIAL_VALUES = {
  closingCostMeta: [],
  sellerClosingCostMeta: [],
  sellerOtherCostMeta: [],
  compensationMeta: [],
  otherCostMeta: [],
  totalClosingMeta: {
    totalAllCostsAndCredits_total: 0,
    totalAllPaymentToBuyer_total: 0,
    totalClosingCosts_total: 0,
    totalEstimatedCashToSeller: 0,
  },
  addressMeta: {
    address: "",
    addressIncludesUnit: false,
    street_no: "",
    street_name: "",
    city: "",
    state: "",
    closing_state: "",
    zip: "",
    unitNumber: "",
    closing_county: "",
  },
  loanInfoMeta: {
    adjusted: 0,
    amount: 0,
    annualPropertyTax: 0,
    assessorsTaxRate: 0,
    autoEstimateLoan: false,
    buyersLoanType: "Conventional",
    buyerDownPayment: 0,
    conventionalPercent: 80,
    conventionalPercent2: 0,
    estimatedTaxProrations: 0,
    interestRate1: 4.2500,
    interestRate2: 0,
    termsInYears1: 30,
    termsInYears2: 0,
    addDueYear1: 7,
    addDueYear2: 0,
    loansToBePaidBalance1: 0,
    loansToBePaidRate1: 0,
    loansToBePaidBalance2: 0,
    loansToBePaidRate2: 0,
    loansToBePaidBalance3: 0,
    loansToBePaidRate3: 0,
    transferTaxRatePer: 0,
  },
  preparedBy: "",
  preparedFor: "New Client",
  salesPrice: "0.00",
  titleSettlementMeta: {
    lendersTitleAmount: "0.00",
    lendersTitlePayor: "Seller",
    lendersTitleSeller: "0.00",
    ownersTitleAmount: "0.00",
    ownersTitlePayor: "Seller",
    ownersTitleSeller: "0.00",
    escrowSettlementAmount: "0.00",
    escrowSettlementPayor: "Seller",
    escrowSettlementSeller: "0.00",
    escrowSettlementSplit: "0.00",
    salesTaxRate: "0.00",
  },

};

/**
 * parseCurrency
 * -------------
 * Converts "$1,234.56" or "1,234.56" to 1234.56 safely.
 * Always returns a number (0 if invalid).
 */
export const parseCurrency = (val) => {
  const n = Number(String(val ?? "").replace(/[^0-9.-]+/g, ""));
  return Number.isFinite(n) ? n : 0;
};

/**
 * cleanNumber
 * -----------
 * Keeps only digits and '.' so inputs can be sanitized easily.
 * Useful for numeric input fields before formatting.
 */
export const cleanNumber = (v) => {
  if (v === undefined || v === null) return "";
  const s = typeof v === "string" ? v : String(v);
  return s.replace(/[^0-9.]/g, "");
};

/**
 * Date helpers
 * ------------
 * Used to prefill estimatedClosingDate (today + 30 days).
 * Kept here to avoid duplication.
 */
const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

const toDateStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;

export const getDefaultEstDate = () => toDateStr(addDays(new Date(), 30));

/**
 * useToast
 * --------
 * Small toast helper:
 * - showToast({ message, type })
 * - auto hides after timeout
 * This stays independent of your global Toast component.
 */
/**
 * populateAddressFields
 * --------------------
 * Common utility to populate address fields in RHF forms.
 * Centralizes address field mapping and validation logic.
 *
 * @param {Object} addressData - Normalized address data object
 * @param {Function} setValue - RHF setValue function
 * @returns {boolean} - Success status
 */
export const populateAddressFields = (addressData, setValue, mode) => {
 // console.log('🏠 [populateAddressFields] Populating address fields with data:', addressData);

  if (!addressData || !setValue) {
 //   console.warn('⚠️ [populateAddressFields] Missing address data or setValue function');
    return false;
  }

  try {
    // Define field mappings for clean organization
    const fieldMappings = {
      'addressMeta.address': addressData.address || addressData.formattedAddress || '',
      'addressMeta.addressIncludesUnit': addressData.addressIncludesUnit || false,
      'addressMeta.street_no': addressData.street_no || '',
      'addressMeta.street_name': addressData.street_name || '',
      'addressMeta.city': addressData.city || '',
      'addressMeta.state': addressData.state || '',
      'addressMeta.closing_state': addressData.state || '', // Also set closing_state for SetCountyModal
      'addressMeta.zip': addressData.zip || '',
      'addressMeta.unitNumber': addressData.unitNumber || '',
      'addressMeta.closing_county': addressData.closing_county || '',
    };

    // Set all address fields at once
    Object.entries(fieldMappings).forEach(([fieldPath, value]) => {
      setValue(fieldPath, value, { shouldValidate: false });
    });

    // Handle unit toggle logic
    const hasUnitNumber = addressData.unitNumber && addressData.unitNumber.trim() !== '';
    if (hasUnitNumber) {
      //  console.log('✅ [populateAddressFields] Auto-checking unit toggle for unit number:', addressData.unitNumber);
      setValue("showSiteUnit", true, { shouldValidate: false });
    }

  //  console.log('✅ [populateAddressFields] All address fields populated successfully');
    return true;

  } catch (error) {
 //   console.error('❌ [populateAddressFields] Error populating address fields:', error);
    return false;
  }
};

export const useToast = (timeout = 5000) => {
  const [toast, setToast] = useState({
    message: "",
    visible: false,
    type: "success",
  });

  const showToast = useCallback(
    ({ message, type = "success" }) => {
      setToast({ message, visible: true, type });

      setTimeout(() => {
        setToast({ message: "", visible: false, type: "success" });
      }, timeout);
    },
    [timeout]
  );

  return { toast, showToast };
};
